import React, { FC, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import { openFileDialog, T, uploadFile } from 'src/shared/helpers';
import SendIcon from 'src/shared/material_icons/content/send.svg';
import AttachFileIcon from 'src/shared/material_icons/editor/attach_file.svg';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import { useValidationContext, validateRequiredTrimmed, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';
import { BasicButton, GhostButton, PrimaryButton } from '../buttons';
import { DropdownContent, DropdownFooter, DropdownHeader, useDropdown } from '../dropdown';
import { FormControl } from '../formControl';
import { DropdownPlacement } from '../hooks/useDropdown';
import { Icon } from '../icon';
import { showErrorNotification, showSuccessNotification, showWarningNotification } from '../notificationToaster';
import Stack from '../stack/stack';
import { TextArea } from '../textArea';
import { supportTicketDropdownMutation } from './__generated__/supportTicketDropdownMutation.graphql';
import { supportTicketDropdownQuery } from './__generated__/supportTicketDropdownQuery.graphql';

const ZENDESK_MAX_ATTACHMENTS = 500;
const ZENDESK_MAX_ATTACHMENT_SIZE_BYTES = 50 * 1024 * 1024;

type Attachment = {
    file: File;
    url?: string;
    isUploading: boolean;
};

type TriggerRenderProps = {
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    isOpen: boolean;
};

interface SupportTicketDropdownTriggerProps {
    children: (props: TriggerRenderProps) => React.ReactNode;
    placement?: DropdownPlacement;
}

const FormBody = styled.div`
    width: 420px;
    max-width: calc(100vw - 32px);
`;

const IntroText = styled.p`
    margin: 0;
    color: #6b7280;
    font-size: 13px;
    line-height: 18px;
`;

const RequiredMark = styled.span`
    color: #d32f2f;
`;

const MessageTextArea = styled(TextArea)`
    width: 100%;
    min-height: 112px;
    resize: vertical;
`;

const AttachmentsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const AttachmentRow = styled.div`
    display: grid;
    grid-template-columns: 18px minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #fafafa;
    color: #374151;
    font-size: 12px;
`;

const AttachmentName = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const AttachmentStatus = styled.span`
    color: #6b7280;
    font-size: 11px;
`;

const LoadingContent = styled.div`
    width: 420px;
    max-width: calc(100vw - 32px);
    padding: 16px;
    color: #6b7280;
    font-size: 13px;
`;

const ActionsFooter = styled(DropdownFooter)`
    justify-content: flex-end;
`;

const query = graphql`
    query supportTicketDropdownQuery {
        seller {
            email
            emailContact
            name
        }
        seller {
            sellerID
        }
        subscription {
            inTrial
            planName
            profileName
        }
        salesChannels(status: ACTIVE, ignoreNoneECommerce: true) {
            id
            ecommerceSettings {
                ecommerceName
                remoteUrl
            }
        }
    }
`;

const mutation = graphql`
    mutation supportTicketDropdownMutation($input: CreateZendeskTicketInput!) {
        createZendeskTicket(input: $input) {
            ticketId
            success
            errorMessage
        }
    }
`;

const formatFileSize = (size: number) => {
    if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;

    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const SupportTicketForm: FC<{ onSubmitted: () => void }> = ({ onSubmitted }) => {
    const validationContext = useValidationContext();
    const data = useLazyLoadQuery<supportTicketDropdownQuery>(query, {}, { fetchPolicy: 'store-or-network' });
    const [createTicket, isCreatingTicket] = useMutation<supportTicketDropdownMutation>(mutation);
    const email = data.seller?.emailContact || data.seller?.email || '';
    const [message, setMessage] = useState('');
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const attachmentsRef = useRef<Attachment[]>([]);

    useEffect(() => {
        attachmentsRef.current = attachments;
    }, [attachments]);

    useEffect(() => {
        return () => {
            attachmentsRef.current = [];
        };
    }, []);

    const subscriptionStatus = data.subscription?.inTrial ? 'Trial' : data.subscription ? 'Active' : 'No Subscription';
    const subscriptionPlan = data.subscription?.planName || 'N/A';
    const salesChannelsList =
        data.salesChannels
            ?.map((channel) => `${channel.ecommerceSettings?.ecommerceName || 'Unknown'} (${channel.ecommerceSettings?.remoteUrl || 'N/A'})`)
            .join('\n') || 'None';
    const hasUploadingAttachments = attachments.some((attachment) => attachment.isUploading);
    const isSubmitDisabled = hasUploadingAttachments || isCreatingTicket;

    const handleAddAttachments = useCallback(() => {
        openFileDialog(
            async (files) => {
                const availableSlots = ZENDESK_MAX_ATTACHMENTS - attachmentsRef.current.length;
                if (availableSlots <= 0) {
                    showWarningNotification({
                        message: T._('You have reached the attachment limit for this request.', 'Admin')
                    });
                    return;
                }

                const filesWithinLimit = files.slice(0, availableSlots);
                if (files.length > availableSlots) {
                    showWarningNotification({
                        message: T._('Some files were not added because the attachment limit was reached.', 'Admin')
                    });
                }

                const validFiles = filesWithinLimit.filter((file) => file.size <= ZENDESK_MAX_ATTACHMENT_SIZE_BYTES);
                const rejectedFiles = filesWithinLimit.filter((file) => file.size > ZENDESK_MAX_ATTACHMENT_SIZE_BYTES);

                if (rejectedFiles.length > 0) {
                    showErrorNotification({
                        message: T._('Attachments cannot exceed 50 MB per file.', 'Admin')
                    });
                }

                if (validFiles.length === 0) return;

                const newAttachments = validFiles.map((file) => ({
                    file,
                    isUploading: true
                }));

                setAttachments((current) => [...current, ...newAttachments]);

                for (const attachment of newAttachments) {
                    try {
                        const uploadedUrl = await uploadFile(attachment.file, undefined, true);
                        if (!uploadedUrl) throw new Error('Upload returned no URL');

                        setAttachments((current) =>
                            current.map((item) => (item.file === attachment.file ? { ...item, url: uploadedUrl, isUploading: false } : item))
                        );
                    } catch {
                        setAttachments((current) => current.filter((item) => item.file !== attachment.file));
                        showErrorNotification({
                            message: T._('Failed to upload one of the selected files.', 'Admin')
                        });
                    }
                }
            },
            '*',
            true
        );
    }, []);

    const handleRemoveAttachment = useCallback((file: File) => {
        setAttachments((current) => current.filter((attachment) => attachment.file !== file));
    }, []);

    const handleSubmit = useCallback(async () => {
        if (!validationContext.submit()) return;

        if (hasUploadingAttachments) {
            showWarningNotification({
                message: T._('Please wait until all attachments finish uploading.', 'Admin')
            });
            return;
        }

        try {
            const response = await new Promise<supportTicketDropdownMutation['response']>((resolve, reject) => {
                createTicket({
                    variables: {
                        input: {
                            name: data.seller?.name?.trim() || email,
                            sellerID: data.seller?.sellerID ?? null,
                            email,
                            category: 'OTHER',
                            message: message.trim(),
                            attachmentUrls: attachments.map((attachment) => attachment.url).filter((url): url is string => Boolean(url)),
                            subscriptionStatus,
                            subscriptionPlan,
                            salesChannels: salesChannelsList,
                            websiteURL: null
                        }
                    },
                    onCompleted: (result) => resolve(result),
                    onError: (error) => reject(error)
                });
            });

            if (!response.createZendeskTicket.success) {
                showErrorNotification({
                    message: response.createZendeskTicket.errorMessage || T._('Failed to submit support request.', 'Admin')
                });
                return;
            }

            showSuccessNotification({
                message: T._('Your support request has been submitted successfully.', 'Admin')
            });
            setMessage('');
            setAttachments([]);
            onSubmitted();
        } catch {
            showErrorNotification({
                message: T._('An error occurred while submitting your request. Please try again.', 'Admin')
            });
        }
    }, [
        attachments,
        createTicket,
        data.seller?.sellerID,
        data.seller?.name,
        email,
        hasUploadingAttachments,
        message,
        onSubmitted,
        salesChannelsList,
        subscriptionPlan,
        subscriptionStatus,
        validationContext
    ]);

    return (
        <FormBody>
            <DropdownHeader>{T._('Send us a message', 'Admin')}</DropdownHeader>
            <DropdownContent $maxHeight='min(620px, calc(100vh - 140px))'>
                <Stack $gap='16px' style={{ padding: '14px' }}>
                    <IntroText>{T._('Describe what you need help with and our support team will get back to you by email.', 'Admin')}</IntroText>
                    <FormControl
                        label={
                            <>
                                {T._('Message', 'Admin')} <RequiredMark>*</RequiredMark>
                            </>
                        }
                    >
                        <MessageTextArea
                            value={message}
                            onChange={(event) => setMessage(event.currentTarget.value)}
                            placeholder={T._('How can we help?', 'Admin')}
                            validationKey='supportTicketMessage'
                            validationLabel={T._('Message', 'Admin')}
                            validate={validateRequiredTrimmed}
                            validationMessage={T._('Message is required.', 'Admin')}
                        />
                    </FormControl>
                    <FormControl
                        label={T._('Attachments', 'Admin')}
                        caption={T._('Maximum 50 MB per file. You can attach screenshots, files, or other useful documents.', 'Admin')}
                    >
                        <Stack $gap='10px'>
                            {attachments.length > 0 && (
                                <AttachmentsList>
                                    {attachments.map((attachment) => (
                                        <AttachmentRow key={`${attachment.file.name}-${attachment.file.lastModified}`}>
                                            <Icon>
                                                <AttachFileIcon />
                                            </Icon>
                                            <AttachmentName title={attachment.file.name}>
                                                {attachment.file.name} ({formatFileSize(attachment.file.size)})
                                            </AttachmentName>
                                            {attachment.isUploading ? (
                                                <AttachmentStatus>{T._('Uploading...', 'Admin')}</AttachmentStatus>
                                            ) : (
                                                <GhostButton
                                                    type='button'
                                                    $size='small'
                                                    $type='icon'
                                                    aria-label={T._('Remove attachment', 'Admin')}
                                                    onClick={() => handleRemoveAttachment(attachment.file)}
                                                >
                                                    <Icon>
                                                        <CloseIcon />
                                                    </Icon>
                                                </GhostButton>
                                            )}
                                        </AttachmentRow>
                                    ))}
                                </AttachmentsList>
                            )}
                            <BasicButton type='button' $size='small' onClick={handleAddAttachments} disabled={isCreatingTicket}>
                                <Icon>
                                    <AttachFileIcon />
                                </Icon>
                                {T._('Add attachments', 'Admin')}
                            </BasicButton>
                        </Stack>
                    </FormControl>
                </Stack>
            </DropdownContent>
            <ActionsFooter>
                <PrimaryButton type='button' $size='small' onClick={handleSubmit} disabled={isSubmitDisabled}>
                    <Icon>
                        <SendIcon />
                    </Icon>
                    {isCreatingTicket ? T._('Sending...', 'Admin') : T._('Send', 'Admin')}
                </PrimaryButton>
            </ActionsFooter>
        </FormBody>
    );
};

export const SupportTicketDropdownTrigger: FC<SupportTicketDropdownTriggerProps> = ({ children, placement = 'top-end' }) => {
    const [openDropdown, closeDropdown, isOpen, Dropdown] = useDropdown();

    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            event.preventDefault();
            openDropdown(event.currentTarget, placement);
        },
        [openDropdown, placement]
    );

    return (
        <>
            {children({ onClick: handleClick, isOpen })}
            <Dropdown width='420px' maxWidth='calc(100vw - 32px)' surfacePadding='0' zIndex={120}>
                <Suspense fallback={<LoadingContent>{T._('Loading support form...', 'Admin')}</LoadingContent>}>
                    <ValidationStateProvider>
                        <SupportTicketForm onSubmitted={closeDropdown} />
                    </ValidationStateProvider>
                </Suspense>
            </Dropdown>
        </>
    );
};
